import { useState, useEffect } from "react";
import {
  FaEdit,
  FaCheck,
  FaExternalLinkAlt,
  FaMapPin,
  FaBuilding,
  FaTag,
} from "react-icons/fa";
import { useApi } from "../context/useApi";


const StoreDetails = ({ store, goBack }) => {
  const [editing, setEditing] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);
  const [toast, setToast] = useState({ message: "", type: "" });
  const [lastUpdated, setLastUpdated] = useState(store?.last_updated || null);
  const { apiFetch } = useApi();
  const [form, setForm] = useState({
    store_name: "",
    description: "",
    store_type: "",
    product_categories: [],
    store_image_url: "",
    gst: "",
    location: "",
    address: "",
    is_active: true,
    tags: [],
    remarks: "",
    coords: { lat: null, lng: null },
  });

  useEffect(() => {
    if (!store) return;
    setForm({
      store_name: store.store_name || "",
      description: store.description || "",
      store_type: store.store_type || "",
      product_categories: store.product_categories || [],
      store_image_url: store.store_image_url || "",
      gst: store.gst || "",
      location: store.location || "",
      address: store.address || "",
      is_active: store.is_active ?? true,
      tags: store.tags || [],
      remarks: store.remarks || "",
      coords: store.coords || { lat: null, lng: null },
    });
    setLastUpdated(store.last_updated || null);
  }, [store]);

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast({ message: "", type: "" }), 3000);
  };

const handleSave = async () => {
  setLoadingSave(true);
  try {
    const res = await apiFetch(
      `https://admin-backend-fbuuh6fo3q-el.a.run.app/vendors/stores/${store.store_id}`,
      {
        method: "PUT",
        body: JSON.stringify(form),
      }
    );
    if (!res) return;
    const data = await res.json();
    if (res.ok) {
      setEditing(false);
      setLastUpdated(new Date().toISOString());
      Object.assign(store, form);
      showToast("Store details saved successfully", "success");
    } else {
      showToast(data.error || "Failed to save store details", "error");
    }
  } catch (err) {
    console.error("Store update error:", err);
    showToast("Failed to save store details", "error");
  } finally {
    setLoadingSave(false);
  }
};

  const handleCancel = () => {
    setForm({
      store_name: store.store_name || "",
      description: store.description || "",
      store_type: store.store_type || "",
      product_categories: store.product_categories || [],
      store_image_url: store.store_image_url || "",
      gst: store.gst || "",
      location: store.location || "",
      address: store.address || "",
      is_active: store.is_active ?? true,
      tags: store.tags || [],
      remarks: store.remarks || "",
      coords: store.coords || { lat: null, lng: null },
    });
    setEditing(false);
  };

  const getLocationLink = () => {
    if (form.coords?.lat && form.coords?.lng) {
      return `https://www.google.com/maps/search/?api=1&query=${form.coords.lat},${form.coords.lng}`;
    }
    return form.location || null;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Compact Header */}
      <div className="bg-white border-b shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={goBack}
                className="text-gray-600 hover:text-gray-900 font-medium text-sm"
              >
                ← Stores
              </button>
              <div className="h-6 w-px bg-gray-300"></div>
              <div className="flex items-center gap-3">
                <div>
                  <h1 className="text-xl font-semibold text-gray-900">
                    {form.store_name || "Untitled Store"}
                  </h1>
                  <p className="text-xs text-gray-500 mt-0.5">
                    ID: {store.store_id}
                  </p>
                </div>
                <span
                  className={`px-2.5 py-1 text-xs font-medium rounded-full ${
                    form.is_active
                      ? "bg-green-100 text-green-700"
                      : "bg-gray-100 text-gray-700"
                  }`}
                >
                  {form.is_active ? "Active" : "Inactive"}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {getLocationLink() && (
                <a
                  href={getLocationLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 py-2 text-sm text-gray-700 hover:text-gray-900 flex items-center gap-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  <FaMapPin size={14} />
                  View Location
                </a>
              )}
              {!editing ? (
                <button
                  onClick={() => setEditing(true)}
                  className="px-4 py-2 text-sm font-medium bg-gray-900 text-white rounded-lg hover:bg-gray-800 flex items-center gap-2"
                >
                  <FaEdit size={14} />
                  Edit
                </button>
              ) : (
                <>
                  <button
                    onClick={handleCancel}
                    className="px-4 py-2 text-sm font-medium border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={loadingSave}
                    className="px-4 py-2 text-sm font-medium bg-gray-900 text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 flex items-center gap-2"
                  >
                    <FaCheck size={14} />
                    {loadingSave ? "Saving..." : "Save Changes"}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="grid grid-cols-12 gap-6">
          {/* Left Column - Image & Meta */}
          <div className="col-span-3 space-y-4">
            {/* Store Image Card */}
            <div className="bg-white rounded-lg shadow-sm p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                Store Image
              </h3>
              {form.store_image_url ? (
                <img
                  src={form.store_image_url}
                  alt={form.store_name}
                  className="w-full rounded-lg border border-gray-200"
                />
              ) : (
                <div className="w-full aspect-square bg-gray-100 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center">
                  <div className="text-center">
                    <FaBuilding
                      className="mx-auto text-gray-400 mb-2"
                      size={24}
                    />
                    <p className="text-xs text-gray-500">No image</p>
                  </div>
                </div>
              )}
              {editing && (
                <input
                  type="text"
                  value={form.store_image_url}
                  onChange={(e) =>
                    setForm({ ...form, store_image_url: e.target.value })
                  }
                  placeholder="Image URL"
                  className="w-full mt-3 px-3 py-2 text-xs border border-gray-300 rounded-md"
                />
              )}
            </div>

            {/* Metadata Card */}
            <div className="bg-white rounded-lg shadow-sm p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                Metadata
              </h3>
              <div className="space-y-3 text-xs">
                <div>
                  <p className="text-gray-500">Store Type</p>
                  <p className="text-gray-900 mt-1">{form.store_type || "-"}</p>
                </div>
                <div>
                  <p className="text-gray-500">Last Updated</p>
                  <p className="text-gray-900 mt-1">
                    {lastUpdated
                      ? new Date(lastUpdated).toLocaleDateString()
                      : "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Categories</p>
                  <p className="text-gray-900 mt-1">
                    {form.product_categories?.length || 0}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Content */}
          <div className="col-span-9 space-y-6">
            {/* Basic Information Card */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-base font-semibold text-gray-900 mb-4">
                Store Information
              </h2>
              <div className="grid grid-cols-2 gap-4">
                <Field
                  label="Store Name"
                  value={form.store_name}
                  editing={editing}
                  onChange={(v) => setForm({ ...form, store_name: v })}
                  required
                />
                <Field
                  label="Store Type"
                  value={form.store_type}
                  editing={editing}
                  type="select"
                  options={["Online", "Offline", "Hybrid"]}
                  onChange={(v) => setForm({ ...form, store_type: v })}
                />
                <Field
                  label="GST Number"
                  value={form.gst}
                  editing={editing}
                  onChange={(v) => setForm({ ...form, gst: v })}
                />
                <Field
                  label="Status"
                  value={form.is_active ? "Active" : "Inactive"}
                  editing={editing}
                  type="select"
                  options={["Active", "Inactive"]}
                  onChange={(v) =>
                    setForm({ ...form, is_active: v === "Active" })
                  }
                />
              </div>
            </div>

            {/* Description Card */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-base font-semibold text-gray-900 mb-4">
                Description
              </h2>
              <Field
                value={form.description}
                editing={editing}
                type="textarea"
                onChange={(v) => setForm({ ...form, description: v })}
                rows={3}
              />
            </div>

            {/* Location Card */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center gap-2 mb-4">
                <FaMapPin size={16} className="text-gray-600" />
                <h2 className="text-base font-semibold text-gray-900">
                  Location & Address
                </h2>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Field
                    label="Address"
                    value={form.address}
                    editing={editing}
                    type="textarea"
                    onChange={(v) => setForm({ ...form, address: v })}
                    rows={2}
                  />
                </div>
                {editing && (
                  <>
                    <Field
                      label="Latitude"
                      value={form.coords?.lat ?? ""}
                      editing={editing}
                      type="number"
                      onChange={(v) =>
                        setForm({
                          ...form,
                          coords: {
                            ...form.coords,
                            lat: v === "" ? null : parseFloat(v),
                          },
                        })
                      }
                    />
                    <Field
                      label="Longitude"
                      value={form.coords?.lng ?? ""}
                      editing={editing}
                      type="number"
                      onChange={(v) =>
                        setForm({
                          ...form,
                          coords: {
                            ...form.coords,
                            lng: v === "" ? null : parseFloat(v),
                          },
                        })
                      }
                    />
                  </>
                )}
                <div className="col-span-2">
                  <Field
                    label="Location URL"
                    value={form.location}
                    editing={editing}
                    onChange={(v) => setForm({ ...form, location: v })}
                    type="url"
                  />
                </div>
                {!editing && getLocationLink() && (
                  <div className="col-span-2">
                    <a
                      href={getLocationLink()}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 text-sm"
                    >
                      <FaExternalLinkAlt size={14} />
                      Open in Google Maps
                    </a>
                  </div>
                )}
              </div>
            </div>

            {/* Product Categories Card */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-base font-semibold text-gray-900 mb-4">
                Product Categories
              </h2>
              {editing ? (
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  value={form.product_categories.join(", ")}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      product_categories: e.target.value
                        .split(",")
                        .map((v) => v.trim())
                        .filter(Boolean),
                    })
                  }
                  placeholder="Enter categories separated by commas"
                />
              ) : (
                <div className="flex flex-wrap gap-2">
                  {form.product_categories.length > 0 ? (
                    form.product_categories.map((cat, i) => (
                      <span
                        key={i}
                        className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-full text-sm font-medium"
                      >
                        {cat}
                      </span>
                    ))
                  ) : (
                    <span className="text-gray-500 text-sm">
                      No categories assigned
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Tags Card */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center gap-2 mb-4">
                <FaTag size={16} className="text-gray-600" />
                <h2 className="text-base font-semibold text-gray-900">Tags</h2>
              </div>
              {editing ? (
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  value={form.tags.join(", ")}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      tags: e.target.value
                        .split(",")
                        .map((t) => t.trim())
                        .filter(Boolean),
                    })
                  }
                  placeholder="Enter tags separated by commas"
                />
              ) : (
                <div className="flex flex-wrap gap-2">
                  {form.tags.length > 0 ? (
                    form.tags.map((tag, i) => (
                      <span
                        key={i}
                        className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                      >
                        {tag}
                      </span>
                    ))
                  ) : (
                    <span className="text-gray-500 text-sm">
                      No tags assigned
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Remarks Card */}
            {(form.remarks || editing) && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-base font-semibold text-gray-900 mb-4">
                  Remarks & Notes
                </h2>
                <Field
                  value={form.remarks}
                  editing={editing}
                  type="textarea"
                  onChange={(v) => setForm({ ...form, remarks: v })}
                  rows={3}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Toast */}
      {toast.message && (
        <div
          className={`fixed bottom-6 right-6 px-5 py-3 rounded-lg shadow-lg text-white text-sm font-medium ${
            toast.type === "success" ? "bg-gray-900" : "bg-red-600"
          }`}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
};

// Reusable Field Component
const Field = ({
  label,
  value,
  editing,
  onChange,
  type = "text",
  options = [],
  required = false,
  rows = 3,
}) => {
  if (!label && !editing) {
    return (
      <p className="text-sm text-gray-900 whitespace-pre-wrap">
        {value || "-"}
      </p>
    );
  }

  return (
    <div>
      {label && (
        <label className="block text-xs font-medium text-gray-700 mb-1.5">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      {editing ? (
        type === "select" ? (
          <select
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
          >
            <option value="">Select {label}</option>
            {options.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        ) : type === "textarea" ? (
          <textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
            rows={rows}
          />
        ) : (
          <input
            type={type}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
            step={type === "number" ? "any" : undefined}
          />
        )
      ) : (
        <p className="text-sm text-gray-900 whitespace-pre-wrap">
          {String(value || "-")}
        </p>
      )}
    </div>
  );
};

export default StoreDetails